"use client";

import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "@/lib/i18n/navigation";

type Props = {
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: "patient" | "pro" | "admin";
  /** Variant du header : "header" (cyan plein) ou "pro" (light). */
  variant?: "header" | "pro";
};

type MenuItem = { href: string; icon: string; label: string; external?: boolean };
type MenuConfig = { sectionLabel: string; items: MenuItem[]; roleLabel: string };

// Séparation stricte des espaces (décision produit 2026-05-13) :
// chaque rôle voit uniquement le menu correspondant à son espace.
// Un admin ne voit pas "Mon compte patient", un pro ne voit pas l'espace admin,
// un patient ne voit ni admin ni pro. Pour qu'un admin teste l'espace pro
// d'un médecin, il utilise la fonction d'impersonation depuis /admin/medecins/[id].
const MENU: Record<"admin" | "pro" | "patient", MenuConfig> = {
  admin: {
    sectionLabel: "Espace admin",
    roleLabel: "Administrateur",
    items: [
      { href: "/admin", icon: "🏠", label: "Tableau de bord" },
      { href: "/admin/medecins", icon: "👨‍⚕️", label: "Médecins" },
      { href: "/admin/utilisateurs", icon: "👥", label: "Utilisateurs" },
      { href: "/admin/leads", icon: "📩", label: "Demandes" },
      { href: "/admin/articles", icon: "📰", label: "Articles" },
      { href: "/admin/parametres", icon: "⚙️", label: "Paramètres" },
    ],
  },
  pro: {
    sectionLabel: "Espace praticien",
    roleLabel: "Praticien",
    items: [
      { href: "/espace-pro/dashboard", icon: "🏠", label: "Tableau de bord" },
      { href: "/espace-pro/agenda", icon: "📅", label: "Agenda" },
      { href: "/espace-pro/rendez-vous", icon: "🗓", label: "Rendez-vous" },
      { href: "/espace-pro/leads", icon: "📩", label: "Demandes" },
      { href: "/espace-pro/avis", icon: "⭐", label: "Avis" },
      { href: "/espace-pro/profil", icon: "👤", label: "Mon profil public" },
      { href: "/espace-pro/parametres", icon: "⚙️", label: "Paramètres" },
    ],
  },
  patient: {
    sectionLabel: "Mon compte",
    roleLabel: "Patient",
    items: [
      { href: "/mon-compte", icon: "🏠", label: "Tableau de bord" },
      { href: "/mon-compte/rendez-vous", icon: "📆", label: "Mes rendez-vous" },
      { href: "/mon-compte/favoris", icon: "⭐", label: "Mes favoris" },
      { href: "/mon-compte/ordonnances", icon: "📄", label: "Mes ordonnances" },
      { href: "/mon-compte/messagerie", icon: "💬", label: "Messagerie" },
      { href: "/mon-compte/profil", icon: "⚙️", label: "Paramètres" },
    ],
  },
};

const ROLE_BADGE: Record<"admin" | "pro" | "patient", { bg: string; text: string }> = {
  admin: { bg: "bg-slate-900", text: "text-white" },
  pro: { bg: "bg-[var(--sahha-primary-700)]", text: "text-white" },
  patient: { bg: "bg-emerald-100", text: "text-emerald-800" },
};

/**
 * Bouton avatar + prénom + dropdown role-aware.
 * Affiché à la place de "S'inscrire / Connexion" quand l'utilisateur a une
 * session active. Détection au server-side via getSession() dans Header.tsx,
 * ce composant ne fait que le rendu + dropdown.
 *
 * Sépare strictement les espaces selon role :
 *   admin   → menu admin uniquement
 *   pro     → menu pro uniquement
 *   patient → menu patient uniquement
 */
export function UserMenuButton({ firstName, lastName, email, role, variant = "header" }: Props) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setMounted(true); }, []);

  function computePosition() {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }

  useEffect(() => {
    if (!open) return;
    computePosition();
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    function onResize() { computePosition(); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open]);

  const config = MENU[role];
  const initials = `${(firstName ?? email)[0] ?? "?"}${lastName?.[0] ?? ""}`.toUpperCase();
  const displayName = firstName?.trim() || email.split("@")[0];
  const isHeader = variant === "header";
  const roleBadge = ROLE_BADGE[role];

  async function logout() {
    setOpen(false);
    try {
      // Accept: application/json → réponse 200 DIRECTE (pas 303) : les
      // Set-Cookie de suppression sont appliqués de façon fiable (fix 2026-06-18
      // : « déconnecté mais toujours connecté »). Le cookie session est un JWT
      // stateless → sa suppression est la SEULE chose qui déconnecte.
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: { Accept: "application/json" },
        credentials: "include",
        cache: "no-store",
      });
    } catch {/* noop */}
    window.location.assign("/");
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={
          isHeader
            ? "inline-flex h-8 items-center gap-2 rounded-full bg-white/15 px-1.5 pr-3 text-[13px] font-semibold text-white transition-all hover:bg-white/25"
            : "inline-flex h-8 items-center gap-2 rounded-full bg-slate-100 px-1.5 pr-3 text-[13px] font-semibold text-slate-800 transition-all hover:bg-slate-200"
        }
      >
        <span
          className={
            isHeader
              ? "flex h-6 w-6 items-center justify-center rounded-full bg-white text-[11px] font-bold text-[var(--sahha-primary-700)]"
              : "flex h-6 w-6 items-center justify-center rounded-full bg-[var(--sahha-primary-600)] text-[11px] font-bold text-white"
          }
        >
          {initials}
        </span>
        <span className="max-w-[100px] truncate">{displayName}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && mounted && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: "fixed", top: pos.top, right: pos.right }}
          className="z-[2000] min-w-[260px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl ring-1 ring-black/5"
        >
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <p className="min-w-0 truncate text-[13px] font-semibold text-slate-900">
                {firstName} {lastName}
              </p>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${roleBadge.bg} ${roleBadge.text}`}>
                {config.roleLabel}
              </span>
            </div>
            <p className="mt-0.5 truncate text-[12px] text-slate-500">{email}</p>
          </div>
          <nav className="py-1.5">
            <div className="px-4 pt-2 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {config.sectionLabel}
            </div>
            {config.items.map((item) =>
              item.external ? (
                <a
                  key={item.href}
                  href={item.href}
                  className="flex items-center gap-2.5 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  <span>{item.icon}</span> {item.label}
                </a>
              ) : (
                <Link
                  key={item.href}
                  href={item.href as never}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  <span>{item.icon}</span> {item.label}
                </Link>
              ),
            )}

            <div className="my-1 h-px bg-slate-100" />
            <button
              type="button"
              onClick={logout}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13px] font-medium text-slate-700 hover:bg-slate-50"
            >
              <span>🚪</span> Déconnexion
            </button>
          </nav>
        </div>,
        document.body,
      )}
    </div>
  );
}
